import { useAtom } from "jotai";
import { authAtom, businessOwnedVenuesAtom, remindLocationAtom } from "./atoms";
import type { MyVenuesResponse } from "../../apis/business";
import { Storage } from "../../storage";
import { useCallback, useEffect, useMemo } from "react";
import User from "../../types/user";
import { userSavedPlacesAtom } from "../saved-places/atoms";
import { useQueryClient } from "@tanstack/react-query";

export function useAuth() {
  const queryClient = useQueryClient();
  const [{ data, error, isLoading, isFetching, refetch }] = useAtom(authAtom);
  const [
    {
      data: savedPlacesData,
      error: savedPlacesError,
      isLoading: savedPlacesLoading,
      isFetching: savedPlacesFetching,
      refetch: savedPlacesRefetch,
    },
  ] = useAtom(userSavedPlacesAtom);

  const [
    {
      data: businessVenuesResponse,
      error: businessVenuesError,
      isLoading: businessVenuesLoading,
      isFetching: businessVenuesFetching,
    },
  ] = useAtom(businessOwnedVenuesAtom);

  const [remindLocation, setRemindLocation] = useAtom(remindLocationAtom);

  const businessOwnedVenues: MyVenuesResponse["data"] =
    businessVenuesResponse?.data ?? [];

  const businessVenuesAnyLoading =
    businessVenuesLoading || businessVenuesFetching;

  useEffect(() => {
    if (error) {
      console.log("error", error);
      logout();
    }
  }, [error]);

  const isAutoTagEnabledForComplexVenues = useMemo(() => {
    return (
      data?.roles?.aiAccess?.tags?.enabled &&
      data?.roles?.aiAccess?.tags?.venues.includes("complex") &&
      data?.roles?.aiAccess?.tags?.flow === "ai_first"
    );
  }, [data?.roles?.aiAccess]);

  const isAltTextEnabledForComplexVenues = useMemo(() => {
    return (
      data?.roles?.aiAccess?.alt_text?.enabled &&
      data?.roles?.aiAccess?.alt_text?.venues.includes("complex") &&
      data?.roles?.aiAccess?.alt_text?.flow === "ai_first"
    );
  }, [data?.roles?.aiAccess]);

  const isAltTextEnabledForBasicVenues = useMemo(() => {
    return (
      data?.roles?.aiAccess?.alt_text?.enabled &&
      data?.roles?.aiAccess?.alt_text?.venues.includes("basic") &&
      data?.roles?.aiAccess?.alt_text?.flow === "ai_first"
    );
  }, [data?.roles?.aiAccess]);

  const isAutoTagEnabledForBasicVenues = useMemo(() => {
    return (
      data?.roles?.aiAccess?.tags?.enabled &&
      data?.roles?.aiAccess?.tags?.venues.includes("basic") &&
      data?.roles?.aiAccess?.tags?.flow === "ai_first"
    );
  }, [data?.roles?.aiAccess]);

  const isLoggedIn = !!data;

  // const logout = () => {
  //   Storage.delete("currentUser");
  //   refetch();
  // };

  const logout = useCallback(async () => {
    Storage.delete("currentUser");
    queryClient.clear();
    setTimeout(() => {
      refetch();
    }, 300);
  }, [refetch, data?.accessToken]);

  const isMyBusiness = useCallback(
    (businessId: string | number | undefined | null): boolean => {
      if (!data?.roles?.isBusinessUser) {
        return false;
      }
      if (businessId === undefined || businessId === null) {
        return false;
      }
      const needle = String(businessId).trim();
      if (needle === "") {
        return false;
      }
      return businessOwnedVenues.some((v) => String(v.id) === needle);
    },
    [data?.roles?.isBusinessUser, businessOwnedVenues],
  );

  /** True while any user-scoped request in this hook is in flight. */
  const isCurrentUserLoading = useMemo(
    () =>
      isLoading ||
      isFetching ||
      savedPlacesLoading ||
      savedPlacesFetching ||
      businessVenuesAnyLoading,
    [
      isLoading,
      isFetching,
      savedPlacesLoading,
      savedPlacesFetching,
      businessVenuesAnyLoading,
    ],
  );

  return {
    isLoggedIn,
    user: data as User,
    error,
    isLoading,
    isFetching,
    isCurrentUserLoading,
    logout,
    remindLocation,
    setRemindLocation,
    isMapper:
      data?.roles?.isSociabilityMapper || data?.roles?.isSociabilityTeam,
    isBusinessUser: data?.roles?.isBusinessUser,
    businessOwnedVenues,
    businessVenuesLoading: businessVenuesAnyLoading,
    businessVenuesError,
    isMyBusiness,
    isDerbyUser: data?.roles?.isDerbyUser,
    savedPlacesData,
    savedPlacesError,
    savedPlacesLoading,
    savedPlacesFetching,
    savedPlacesRefetch,
    savedPlacesIds:
      savedPlacesData?.saved_places?.flatMap((place) =>
        place.saved_place_details.map((detail) => detail.business_id),
      ) || [], // Add Optional Chaining and default value
    isAiAccessUser: data?.roles?.isAiAccessUser, //Obsolete
  };
}
